import { useEffect, useMemo, useState } from 'react'

import type { ComicPageRegenerateMode } from '../../api/comicEpisodeJobs'
import { wholePagePreviewAspectClass } from '../../lib/comicPageLayout'
import type { ComicPage } from '../../lib/comicTypes'
import { comicPageToLightboxItems, lightboxIndexForPageCandidate } from '../../lib/comicPageLightbox'
import { mediaDisplayUrl } from '../../lib/resolvedMediaImage'
import { ChromePrimaryButton, ChromeToolbarButton } from '../ChromeActions'
import { chromeToggleChipClass } from '../ChromeSurface'
import { ImageLightbox } from '../ImageLightbox'
import { ThemeModal } from '../modal'
import { ThemedImage } from '../ThemedImage'
import { comicPanelRoundSm } from './comicPanelTokens'

type Props = {
  open: boolean
  angular: boolean
  pageTitle: string
  page: ComicPage | null
  submitting?: boolean
  /** 从候选弹窗等入口打开时，预选修改底图 */
  initialCandidateId?: string | null
  onClose: () => void
  onConfirm: (payload: {
    mode: ComicPageRegenerateMode
    editPrompt: string
    candidateId: string | null
  }) => void
}

function editBaseThumbClass(angular: boolean, selected: boolean): string {
  return [
    'relative block overflow-hidden bg-surface-muted transition-[box-shadow]',
    comicPanelRoundSm(angular),
    selected
      ? 'shadow-[inset_0_0_0_2px_var(--accent)]'
      : 'shadow-[inset_0_0_0_1px_var(--line)] hover:shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--accent)_55%,var(--line))]',
  ].join(' ')
}

function editBasePreviewFrameClass(angular: boolean, filled: boolean): string {
  return [
    'relative w-full overflow-hidden bg-surface-muted',
    comicPanelRoundSm(angular),
    filled
      ? 'shadow-[inset_0_0_0_2px_var(--accent)]'
      : 'border border-dashed border-line bg-surface-muted/40',
  ].join(' ')
}

export function ComicPageEditImageModal({
  open,
  angular,
  pageTitle,
  page,
  submitting = false,
  initialCandidateId = null,
  onClose,
  onConfirm,
}: Props) {
  const [mode, setMode] = useState<ComicPageRegenerateMode>('edit_text')
  const [editPrompt, setEditPrompt] = useState('')
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const candidates = useMemo(
    () => (page?.pageCandidates ?? []).filter((c) => c.imageUrl?.trim()),
    [page],
  )
  const hasLegacyPageImage = Boolean(page?.pageImageUrl?.trim()) && candidates.length === 0
  const selectedCandidate = candidates.find((c) => c.id === selectedCandidateId) ?? null
  const previewUrl = selectedCandidate?.imageUrl?.trim()
    ? mediaDisplayUrl(selectedCandidate.imageUrl)
    : hasLegacyPageImage && page?.pageImageUrl
      ? mediaDisplayUrl(page.pageImageUrl)
      : null
  const wholePageAspect = page ? wholePagePreviewAspectClass(page.layout) : wholePagePreviewAspectClass('1x1')
  const lightboxItems = comicPageToLightboxItems(page)
  const needsManualPick = candidates.length > 1
  const showPickGrid = needsManualPick || (candidates.length === 0 && !hasLegacyPageImage)

  useEffect(() => {
    if (!open) {
      setMode('edit_text')
      setEditPrompt('')
      setSelectedCandidateId(null)
      setLightboxIndex(null)
      return
    }
    const preferredId =
      (initialCandidateId && candidates.some((c) => c.id === initialCandidateId)
        ? initialCandidateId
        : null) ??
      (page?.selectedPageCandidateId && candidates.some((c) => c.id === page.selectedPageCandidateId)
        ? page.selectedPageCandidateId
        : null)
    if (preferredId) {
      setSelectedCandidateId(preferredId)
    } else if (candidates.length === 1) {
      setSelectedCandidateId(candidates[0]!.id)
    }
  }, [open, candidates, initialCandidateId, page?.selectedPageCandidateId])

  const hasBaseImage = Boolean(previewUrl)
  const canSubmit =
    editPrompt.trim().length > 0 &&
    hasBaseImage &&
    (!needsManualPick || selectedCandidateId != null) &&
    !submitting

  const previewCaption = selectedCandidate && page?.selectedPageCandidateId === selectedCandidate.id
    ? '当前整页定稿'
    : hasLegacyPageImage && !needsManualPick
      ? '当前整页定稿'
      : selectedCandidate
        ? `候选 #${candidates.findIndex((c) => c.id === selectedCandidate.id) + 1}`
        : null

  return (
    <>
      <ThemeModal
        open={open}
        onClose={submitting ? () => {} : onClose}
        title={`改图修字 · ${pageTitle || '未命名页'}`}
        angular={angular}
        size="lg"
        dismissible={!submitting}
        bodyClassName="px-4 py-4 sm:px-5 sm:py-5"
        footer={
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="min-w-0 text-xs text-ink-soft">
              {needsManualPick && !selectedCandidateId
                ? '请从右侧网格选择一张作为修改底图'
                : previewCaption
                  ? `修改底图：${previewCaption}`
                  : hasBaseImage
                    ? '修改底图已就绪'
                    : '请先生成整页成图'}
            </p>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              <ChromeToolbarButton type="button" disabled={submitting} onClick={onClose}>
                取消
              </ChromeToolbarButton>
              <ChromePrimaryButton
                density="compact"
                type="button"
                disabled={!canSubmit}
                onClick={() => {
                  if (!canSubmit) return
                  onConfirm({
                    mode,
                    editPrompt: editPrompt.trim(),
                    candidateId: candidates.length > 0 ? selectedCandidateId : null,
                  })
                }}
              >
                {submitting ? '生成中…' : '生成修正版'}
              </ChromePrimaryButton>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {candidates.length > 0 || hasLegacyPageImage ? (
            <section>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-ink">
                {needsManualPick ? '选择修改底图' : '修改底图'}
              </p>

              {showPickGrid ? (
                <div className="mt-2 flex h-[min(44vh,15.5rem)] items-stretch sm:h-[min(46vh,16.5rem)]">
                  <div className="flex w-[10.25rem] shrink-0 flex-col justify-center sm:w-44">
                    {previewUrl ? (
                      <button
                        type="button"
                        className={[
                          editBasePreviewFrameClass(angular, true),
                          wholePageAspect,
                          'max-h-44 w-full',
                        ].join(' ')}
                        onClick={() => {
                          if (selectedCandidateId) {
                            setLightboxIndex(lightboxIndexForPageCandidate(page, selectedCandidateId))
                          }
                        }}
                        aria-label="放大预览底图"
                      >
                        <ThemedImage
                          src={previewUrl}
                          alt=""
                          angular={angular}
                          className="absolute inset-0 h-full w-full object-cover"
                          fallbackClassName="absolute inset-0 flex h-full w-full items-center justify-center bg-surface-muted text-xs text-ink-soft"
                        />
                        <span className="pointer-events-none absolute bottom-1 right-1 rounded-[var(--radius-sm)] bg-panel/90 px-1 py-px text-[9px] font-semibold text-ink-soft">
                          放大
                        </span>
                      </button>
                    ) : (
                      <div
                        className={[
                          editBasePreviewFrameClass(angular, false),
                          wholePageAspect,
                          'flex max-h-44 w-full items-center justify-center px-1.5 text-center text-[11px] leading-snug text-ink-soft',
                        ].join(' ')}
                      >
                        请从右侧
                        <br />
                        选择底图
                      </div>
                    )}
                    {previewCaption ? (
                      <p className="mt-1.5 text-center text-[11px] text-ink-soft">{previewCaption}</p>
                    ) : null}
                  </div>

                  <div
                    role="separator"
                    aria-orientation="vertical"
                    className="shell-rail-section-divider mx-3 w-px shrink-0 self-stretch border-l border-line bg-transparent"
                  />

                  <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
                    <div className="scroll-region h-full overflow-y-auto pe-0.5">
                      {candidates.length > 0 ? (
                        <ul className="grid grid-cols-3 gap-1 sm:grid-cols-4">
                          {candidates.map((c, i) => {
                            const selected = c.id === selectedCandidateId
                            return (
                              <li key={c.id}>
                                <button
                                  type="button"
                                  className={[
                                    editBaseThumbClass(angular, selected),
                                    wholePageAspect,
                                    'w-full',
                                  ].join(' ')}
                                  onClick={() => setSelectedCandidateId(c.id)}
                                  onDoubleClick={(e) => {
                                    e.preventDefault()
                                    setLightboxIndex(lightboxIndexForPageCandidate(page, c.id))
                                  }}
                                  aria-pressed={selected}
                                >
                                  <img
                                    src={mediaDisplayUrl(c.imageUrl)}
                                    alt=""
                                    className="h-full w-full object-cover"
                                    draggable={false}
                                    loading={i < 8 ? 'eager' : 'lazy'}
                                    decoding="async"
                                  />
                                  {selected ? (
                                    <span className="absolute left-0.5 top-0.5 rounded-[var(--radius-sm)] bg-accent px-1 py-px text-[8px] font-bold leading-none text-white">
                                      底图
                                    </span>
                                  ) : null}
                                  <span className="absolute bottom-0.5 right-0.5 rounded-[var(--radius-sm)] bg-panel/90 px-1 py-px text-[8px] font-bold leading-none tabular-nums text-ink-soft">
                                    {i + 1}
                                  </span>
                                </button>
                              </li>
                            )
                          })}
                        </ul>
                      ) : (
                        <div
                          className={[
                            'flex h-full min-h-[8.5rem] flex-col items-center justify-center gap-1 border border-dashed border-line bg-surface-muted/30 px-3 text-center',
                            comicPanelRoundSm(angular),
                          ].join(' ')}
                        >
                          <p className="text-xs font-medium text-ink-soft">暂无候选图</p>
                          <p className="text-[11px] leading-snug text-ink-soft">请先生成整页成图</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex flex-col items-center">
                  {previewUrl ? (
                    <button
                      type="button"
                      className={[
                        editBasePreviewFrameClass(angular, true),
                        wholePageAspect,
                        'w-[min(100%,8.5rem)] max-h-36',
                      ].join(' ')}
                      onClick={() => {
                        if (selectedCandidateId) {
                          setLightboxIndex(lightboxIndexForPageCandidate(page, selectedCandidateId))
                        } else if (lightboxItems.length > 0) {
                          setLightboxIndex(0)
                        }
                      }}
                      aria-label="放大预览底图"
                    >
                      <ThemedImage
                        src={previewUrl}
                        alt=""
                        angular={angular}
                        className="absolute inset-0 h-full w-full object-cover"
                        fallbackClassName="absolute inset-0 flex h-full w-full items-center justify-center bg-surface-muted text-xs text-ink-soft"
                      />
                      <span className="pointer-events-none absolute bottom-1 right-1 rounded-[var(--radius-sm)] bg-panel/90 px-1 py-px text-[9px] font-semibold text-ink-soft">
                        放大
                      </span>
                    </button>
                  ) : null}
                  {previewCaption ? (
                    <p className="mt-1.5 text-center text-[11px] text-ink-soft">{previewCaption}</p>
                  ) : null}
                </div>
              )}
            </section>
          ) : (
            <p className="text-sm text-ink-soft">请先生成或选择一张整页图</p>
          )}

          <section>
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-ink">修正类型</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                className={chromeToggleChipClass(angular, mode === 'edit_text')}
                onClick={() => setMode('edit_text')}
                disabled={submitting}
              >
                修字/气泡
              </button>
              <button
                type="button"
                className={chromeToggleChipClass(angular, mode === 'edit_image')}
                onClick={() => setMode('edit_image')}
                disabled={submitting}
              >
                轻微改图
              </button>
            </div>
            <p className="mt-1.5 text-xs text-ink-soft">
              {mode === 'edit_text'
                ? '优先修正气泡文字与位置，尽量保持画面不变。'
                : '在保留分镜结构的前提下，按说明微调画面。'}
            </p>
          </section>

          <label className="block text-sm font-semibold text-ink">
            修正说明
            <textarea
              className={[
                'mt-2 min-h-28 w-full resize-y border border-line bg-surface px-3 py-2 text-sm leading-relaxed text-ink outline-none transition',
                'focus:border-accent focus:ring-1 focus:ring-accent',
                angular ? 'rounded-none border-2' : 'rounded-[var(--radius-sm)]',
              ].join(' ')}
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              maxLength={800}
              disabled={submitting}
              placeholder="例如：把第 2 格对白改成“我们现在出发”，气泡移到角色右侧，不要遮挡脸，其他画面保持不变。"
            />
          </label>
        </div>
      </ThemeModal>
      <ImageLightbox
        open={lightboxIndex != null && lightboxItems.length > 0}
        items={lightboxItems}
        index={lightboxIndex ?? 0}
        angular={angular}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
    </>
  )
}
